import type {
  BrandSpecialPrices,
  BrandSpecialUserSegmentIds,
  ProductCategory,
} from '@iheartjane/types';
import type { Dict as MixpanelProperties } from 'mixpanel-browser';

// Account for the fact that the AlgoliaProduct type is readonly
// and that's often what's getting passed in to the tracking functions
interface TrackingBrandSpecialUserSegmentIds {
  applicable_brand_specials_excluded_user_segments?:
    BrandSpecialUserSegmentIds | readonly number[];
  applicable_brand_specials_included_user_segments?:
    BrandSpecialUserSegmentIds | readonly number[];
}

interface CoreProduct extends TrackingBrandSpecialUserSegmentIds {
  aggregate_rating?: number | null;
  applicable_brand_special_ids?: readonly (number | string)[] | null;
  applicable_special_ids?: readonly number[] | null;
  available_weights?: readonly string[] | null;
  brand?: string | null;
  brand_special_prices?: BrandSpecialPrices | null;
  brand_subtype?: string | null;
  category?: ProductCategory | null;
  has_brand_discount?: boolean | null;
  id?: number | string | null;
  kind?: string | null;
  name?: string;
  percent_cbd?: number | string | null;
  percent_thc?: number | string | null;
  primaryPercentCBD?: number | string | null;
  primaryPercentTHC?: number | string | null;
  product_brand_id?: number | string | null;
  product_id?: number | string | null;
  review_count?: number | null;
  root_subtype?: string | null;
  special_id?: number | null;
}

const countDecimals = (num: number): number => {
  if (Math.floor(num.valueOf()) === num.valueOf()) return 0;
  return num.toString().split('.')[1]?.length ?? 0;
};

const isEmpty = (val: unknown): boolean => {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string') return val.length === 0;
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === 'object') return Object.keys(val as object).length === 0;
  return false;
};

/**
 * @param properties a hash of properties associated with a Mixpanel event
 * @returns a hash with all values normalized. Strings are downcased and numbers are truncated to 2 decimal places.
 */
export const normalizeMixpanelPropertyValues = (
  properties: MixpanelProperties = {}
) => {
  const normalizePrimitives = (val: string | number | object) => {
    // Lowercase strings
    if (typeof val === 'string') {
      return val.toLowerCase();
      // Truncate decimal numbers with more than 2 decimal places
    } else if (typeof val === 'number' && countDecimals(val) > 2) {
      return val.toFixed(2);
    }

    return val;
  };

  return Object.entries(properties).reduce(
    (acc: MixpanelProperties, [k, v]) => {
      if (typeof v !== 'number' && typeof v !== 'boolean' && isEmpty(v)) {
        // handles empty {}, [], & "" as well as undefined & null, but also 0/false so escaping that.
        acc[k] = null;
      } else if (Array.isArray(v)) {
        acc[k] = v.map(normalizePrimitives);
      } else {
        acc[k] = normalizePrimitives(v);
      }

      return acc;
    },
    {}
  );
};

const shouldProductShowSpecial = (
  product: TrackingBrandSpecialUserSegmentIds,
  userSegments?: number[]
): boolean => {
  const ALGOLIA_NO_SEGMENTS = -1;
  const includedSegments =
    product.applicable_brand_specials_included_user_segments;
  const excludedSegments =
    product.applicable_brand_specials_excluded_user_segments;

  const noIncludedSegments =
    !includedSegments ||
    includedSegments === ALGOLIA_NO_SEGMENTS ||
    !includedSegments.length;
  const noExcludedSegments =
    !excludedSegments ||
    excludedSegments === ALGOLIA_NO_SEGMENTS ||
    !excludedSegments.length;

  if (!userSegments || userSegments.length === 0) {
    return noIncludedSegments;
  }

  const userIsIncluded = noIncludedSegments
    ? true
    : userSegments.some((segmentId) => includedSegments?.includes(segmentId));

  const userIsExcluded = noExcludedSegments
    ? false
    : userSegments.some((segmentId) => excludedSegments?.includes(segmentId));

  return userIsIncluded && !userIsExcluded;
};

export const coreProductProperties = (
  product: CoreProduct,
  userSegments?: number[]
) => {
  const showProductSpecial = shouldProductShowSpecial(product, userSegments);

  let brandDiscountId: number | string | undefined;
  if (showProductSpecial) {
    brandDiscountId = product.applicable_brand_special_ids?.[0] ?? undefined;
    if (product.brand_special_prices) {
      const brandDiscountPriceWithId = Object.values(
        product.brand_special_prices
      ).filter((price) => !!price?.brand_special_id);
      brandDiscountId =
        brandDiscountPriceWithId.length > 0
          ? (brandDiscountPriceWithId[0]?.brand_special_id ?? brandDiscountId)
          : brandDiscountId;
    }
  }

  const specialIds = product.applicable_special_ids as number[];
  let hasSpecial = specialIds ? specialIds.length > 0 : undefined;
  hasSpecial = product.special_id ? !!product.special_id : hasSpecial;

  const hasMultipleWeights = product.available_weights
    ? product.available_weights.length > 1
    : undefined;

  return {
    brandDiscountId,
    brandId: product.product_brand_id,
    brandName: product.brand,
    hasBrandDiscount:
      (showProductSpecial && product.has_brand_discount) || !!brandDiscountId,
    hasMultipleWeights,
    hasSpecial,
    productId: product.id?.toString() || product.product_id?.toString() || null,
    productName: product.name,
    productBrand: product.brand,
    productKind: product.kind,
    productRootSubtype: product.root_subtype,
    productBrandSubtype: product.brand_subtype,
    productLineage: product.category,
    productPercentCBD: product.percent_cbd || product.primaryPercentCBD,
    productPercentTHC: product.percent_thc || product.primaryPercentTHC,
    productAggregateRating: product.aggregate_rating,
    productReviewCount: product.review_count,
    specialIds,
  };
};
